import React, { FC, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Divider, IconButton, Stack, Typography } from '@mui/material';

import { useGetProcessById } from '@/api/generated';
import CopyIcon from '@/assets/icons/copy.svg?react';
import ButtonGroupToggle from '@/components/fields/ButtonGroupToggle';
import ChipMultiSelect from '@/components/fields/ChipMultiSelect';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { useSnackbar } from '@/hooks/useSnackbar';

import { AccessMethods, getAccessMethodOptions, getInvitationChannelOptions, getLanguageOptions } from './options';

const AccessInvitationTab: FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const params = useParams() as { processId: string };
  const { control } = useFormContext();
  const { showMessage } = useSnackbar();
  const { getPrefixedFieldName } = useFieldPrefix();

  const { data: processData } = useGetProcessById(params.processId, {
    query: { enabled: !!params.processId }
  });
  const publicLink = processData?.publicLink;

  const hasPublicLink = useWatch({
    control,
    name: getPrefixedFieldName('hasPublicLink')
  });
  const accessMethods = useWatch({
    control,
    name: getPrefixedFieldName('accessMethods')
  });

  const handleCopyLink = useCallback(() => {
    if (!publicLink) return;
    navigator.clipboard.writeText(publicLink);
    showMessage(t('common:alerts.copied'));
  }, [publicLink, showMessage, t]);

  return (
    <Stack
      width="100%"
      sx={{
        '& .MuiFormLabel-root': { textAlign: 'start' },
        '& .MuiFormControl-root': { p: 0 }
      }}
      py={3}
      gap={3}
    >
      <Field
        component={ChipMultiSelect}
        name="accessMethods"
        label={t('processSettings.form.accessInvitation.testAccess')}
        withFormControl
        innerProps={{
          options: getAccessMethodOptions(t),
          enforcedValue: true
        }}
      />

      <Divider />
      <Field
        component={DropdownSelect}
        name="talentInvitationLanguage"
        label={t('processSettings.form.accessInvitation.talentInvitationLanguage')}
        withFormControl
        innerProps={{
          options: getLanguageOptions(t),
          isMulti: false,
          usePortal: true,
          sx: { maxWidth: 320 }
        }}
      />
      {accessMethods?.includes(AccessMethods.Link) && (
        <Field
          component={ChipMultiSelect}
          name="invitationChannels"
          label={t('processSettings.form.accessInvitation.talentInvitationaVia')}
          withFormControl
          innerProps={{
            options: getInvitationChannelOptions(t),
            sx: {
              '& .MuiChip-root .MuiChip-icon': {
                marginInlineStart: 2,
                marginInlineEnd: -1
              },
              '& .Mui-selected .MuiChip-icon': {
                color: 'primary.main'
              }
            },
            enforcedValue: true
          }}
        />
      )}
      <Stack direction="row" alignItems="end" gap={3}>
        <Field
          component={ButtonGroupToggle}
          name="hasPublicLink"
          label={t('processSettings.form.accessInvitation.publicLink')}
          withFormControl
          outerSx={{ alignItems: 'start' }}
        />
        {hasPublicLink && publicLink && (
          <Stack direction="row" gap={1} alignItems="end">
            <Typography variant="body3">{publicLink}</Typography>
            <IconButton onClick={handleCopyLink} sx={{ p: 0, width: 12, height: 12 }}>
              <CopyIcon />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default AccessInvitationTab;
